import { useMemo, memo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useConvertToSlug } from "../Func/GlobalFunction";
import { get_thumbnail } from "../utils/variable";
import { HiOutlineSearch, HiX } from "react-icons/hi";

import { fetcher } from "../Func/Fetcher";

const useGlobalSearch = (keyword) => {
  const [searchResults, setSearchResults] = useState({
    artikelkajian: [],
    artikelinfo: [],
    agenda: [],
    liputan: [],
    infografik: [],
    videokajian: [],
    halaman: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!keyword || keyword.trim() === "") {
      setSearchResults({
        artikelkajian: [],
        artikelinfo: [],
        agenda: [],
        liputan: [],
        infografik: [],
        videokajian: [],
        halaman: [],
      });
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await fetcher.GET(
          `/search?q=${encodeURIComponent(keyword)}`,
        );
        if (res.success && res.data && res.data.data) {
          setSearchResults(res.data.data);
        } else if (res.data) {
          // Terkadang bentuk respon berbeda
          setSearchResults(res.data);
        }
      } catch (error) {
        console.error("Error fetching global search:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [keyword]);

  return { searchResults, isLoading };
};

const ResultCard = memo(({ res, route, onClose, categoryLabel }) => {
  const title =
    res.judul || res.mberita_judul || res.judul_artikel || res.title || "";
  const date = res.tanggal || res.mberita_tgl || "Aktual";
  const slug = useConvertToSlug(title);

  return (
    <Link
      to={`/${route}/${res._id || res.id || res.mberita_id}/${slug}`}
      onClick={onClose}
      className="hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg p-2 flex items-start gap-3 transition-colors"
    >
      <img
        src={`${get_thumbnail}${typeof res.gambar === "string" ? res.gambar : res.gambar?.[0] || ""}`}
        alt="thumbnail"
        className="w-16 h-16 object-cover rounded-md flex-shrink-0 bg-neutral-200 dark:bg-neutral-600"
        onError={(e) => {
          e.target.src = "/placeholder-image.jpg";
        }}
      />
      <div className="flex flex-col gap-1 w-full overflow-hidden">
        <span className="text-[10px] font-semibold text-idi-700 dark:text-idi-400 uppercase tracking-wider bg-idi-50 dark:bg-idi-900/30 px-2 py-0.5 rounded-full w-fit">
          {categoryLabel}
        </span>
        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 line-clamp-2 leading-tight">
          {title}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{date}</p>
      </div>
    </Link>
  );
});

const SearchResults = memo(({ result, keyword, onClose, isLoading }) => {
  const categories = useMemo(
    () => [
      { key: "agenda", label: "Agenda", route: "content/agenda" },
      { key: "infografik", label: "Infografik", route: "content/infografik" },
      { key: "liputan", label: "Berita", route: "content/berita" },
      {
        key: "videokajian",
        label: "Video Kajian",
        route: "content/videokajian",
      },
      { key: "artikelkajian", label: "Artikel", route: "content/artikel" },
      {
        key: "artikelinfo",
        label: "Artikel Info",
        route: "content/artikelinfo",
      },
      { key: "halaman", label: "Halaman/Profil", route: "profile" },
    ],
    [],
  );

  const limitedResults = useMemo(
    () =>
      categories.reduce((acc, { key }) => {
        if (result[key]?.length > 0) {
          acc[key] = result[key].slice(0, 3); // Batasi 3 per kategori
        }
        return acc;
      }, {}),
    [result, categories],
  );

  const totalResults = Object.values(limitedResults).reduce(
    (sum, arr) => sum + (arr?.length || 0),
    0,
  );

  if (!keyword) return null;

  return (
    <div className="absolute top-[110%] lg:top-[120%] inset-x-3 lg:inset-x-auto lg:right-0 w-auto lg:w-[450px] bg-white dark:bg-neutral-800 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-neutral-100 dark:border-neutral-700 z-[100] max-h-[70vh] overflow-y-auto flex flex-col transform transition-all duration-200 origin-top lg:origin-top-right">
      <div className="p-4 border-b border-neutral-100 dark:border-neutral-700 flex items-center justify-between sticky top-0 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm z-10">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate pr-4">
          Hasil pencarian untuk{" "}
          <span className="font-semibold text-neutral-900 dark:text-white">
            "{keyword}"
          </span>
        </p>
        <span className="text-xs font-medium bg-neutral-100 dark:bg-neutral-700 px-3 py-1 rounded-full text-neutral-600 dark:text-neutral-300 whitespace-nowrap">
          {totalResults} hasil
        </span>
      </div>

      <div className="flex flex-col p-2 gap-1 min-h-[100px]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-32 gap-3 text-neutral-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-idi-600"></div>
            <p className="text-sm">Mencari...</p>
          </div>
        ) : totalResults > 0 ? (
          categories.map(
            ({ key, label, route }) =>
              limitedResults[key]?.length > 0 && (
                <div key={key} className="flex flex-col gap-1 mb-2 last:mb-0">
                  {limitedResults[key].map((res, index) => (
                    <ResultCard
                      key={`${key}-${res._id || res.id || index}`}
                      res={res}
                      route={route}
                      onClose={onClose}
                      categoryLabel={label}
                    />
                  ))}
                </div>
              ),
          )
        ) : (
          <div className="p-8 flex flex-col items-center justify-center text-neutral-500 dark:text-neutral-400 gap-3">
            <HiOutlineSearch className="w-8 h-8 text-neutral-300 dark:text-neutral-600" />
            <p className="text-sm text-center">
              Tidak menemukan hasil yang cocok untuk "{keyword}".
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

const debounce = (func, delay) => {
  let debounceTimer;
  return (...args) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func(...args), delay);
  };
};

const InputSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const searchContainerRef = useRef(null);

  const { searchResults, isLoading } = useGlobalSearch(debouncedKeyword);

  const handleInputChange = useMemo(
    () => debounce((value) => setDebouncedKeyword(value), 400),
    [],
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setKeyword(val);
    handleInputChange(val);
    if (val.trim()) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clearSearch = () => {
    setKeyword("");
    setDebouncedKeyword("");
    setIsOpen(false);
  };

  return (
    <div
      className="flex items-center justify-end lg:w-auto lg:relative"
      ref={searchContainerRef}
    >
      {/* Search Input Box */}
      <div
        className={`flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-full transition-all duration-300 border border-transparent focus-within:border-idi-500 focus-within:ring-4 focus-within:ring-idi-500/10 dark:focus-within:ring-idi-500/20 group 
        ${
          isOpen
            ? "absolute inset-x-3 top-1/2 -translate-y-1/2 lg:static lg:translate-y-0 w-auto lg:w-64 px-3 py-2 shadow-lg lg:shadow-none z-50"
            : "w-9 h-9 lg:w-64 lg:h-auto justify-center lg:justify-start lg:px-3 lg:py-2"
        }`}
      >
        <HiOutlineSearch
          className="text-neutral-400 group-focus-within:text-idi-600 flex-shrink-0 lg:ml-1"
          size={isOpen ? 18 : 16}
        />
        <input
          type="text"
          placeholder="Cari berita, agenda..."
          value={keyword}
          onChange={handleChange}
          onFocus={() => setIsOpen(true)}
          className={`${isOpen ? "w-full px-2" : "w-0 px-0"} lg:w-full lg:px-2 bg-transparent border-none outline-none focus:ring-0 text-sm text-neutral-800 dark:text-neutral-200 placeholder:text-neutral-400 transition-all duration-300`}
        />
        {keyword && (
          <button
            onClick={clearSearch}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 p-1 flex-shrink-0 transition-colors"
            aria-label="Bersihkan pencarian"
          >
            <HiX size={16} />
          </button>
        )}
      </div>

      {isOpen && (
        <SearchResults
          result={searchResults}
          keyword={debouncedKeyword}
          isLoading={isLoading}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default InputSearch;
